import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { GoogleMap, GoogleMapsModule } from '@angular/google-maps';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { CoreApiService } from '../../../core/service/core-api-service';
import { properties } from '../../../../environments/properties';

@Component({
  selector: 'app-trip-detail',
  standalone: true,
  imports: [CommonModule, GoogleMapsModule, ButtonModule, ToastModule],
  providers: [MessageService],
  templateUrl: './trip-detail.component.html',
  styleUrl: './trip-detail.component.css',
})
export class TripDetailComponent implements OnInit {
  @ViewChild(GoogleMap) googleMap?: GoogleMap;

  idTrip = '';
  trip: any = null;
  loading = true;

  mapCenter: google.maps.LatLngLiteral = { lat: -2.8978, lng: -79.0045 };
  mapZoom = 13;
  mapOptions: google.maps.MapOptions = {
    zoomControl: true,
    styles: [
      { stylers: [{ saturation: -100 }] },
      { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
      { featureType: 'transit', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    ],
  };

  // Pin tipo gota (igual que en la solicitud de carrera).
  private static readonly PIN_PATH =
    'M 0,0 C -2,-20 -10,-22 -10,-30 A 10,10 0 1,1 10,-30 C 10,-22 2,-20 0,0 z';
  private originMarker: google.maps.Marker | null = null;
  private destinoMarker: google.maps.Marker | null = null;
  private routePolyline: google.maps.Polyline | null = null;
  private mapReady = false;
  private mapInstance: google.maps.Map | null = null;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly apiService: CoreApiService,
    private readonly messageService: MessageService,
  ) {}

  ngOnInit(): void {
    this.idTrip = this.route.snapshot.paramMap.get('idTrip') ?? '';
    if (!this.idTrip) {
      this.loading = false;
      return;
    }
    this.loadDetail();
  }

  goBack(): void {
    this.router.navigate(['/admin/ride-request']);
  }

  /** Copia el código de carrera al portapapeles, con confirmación visual. */
  copyCode(): void {
    const code = this.trip?.car_tri_id_trip ?? '';
    if (!code) return;
    navigator.clipboard?.writeText(code).then(
      () =>
        this.messageService.add({
          severity: 'success',
          summary: 'Copiado',
          detail: 'Código de carrera copiado al portapapeles',
        }),
      () =>
        this.messageService.add({
          severity: 'warn',
          summary: 'No se pudo copiar',
          detail: 'Copia el código manualmente',
        }),
    );
  }

  private loadDetail(): void {
    this.apiService.consumirWSGet(
      properties.serviceGetCarTripDetail + '/' + this.idTrip,
      {},
      (res: any) => {
        this.loading = false;
        if (res.estado === properties.codeOK && res.data) {
          this.trip = res.data;
          // Si el mapa ya está listo se pinta ahora; si no, lo hará onMapReady().
          this.renderMap();
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: res.mensaje || 'No se pudo cargar el detalle de la carrera',
          });
        }
      },
    );
  }

  /** Distancia en millas truncada a 2 decimales (p. ej. 8.8713… → "8.87"). */
  get distanceMiles(): string {
    const v = Number(this.trip?.car_tri_distance ?? 0);
    return (Math.trunc(v * 100) / 100).toFixed(2);
  }

  /** Clase CSS del badge según el estado (case-insensitive). */
  statusClass(): string {
    const s = (this.trip?.st_name ?? '').toString().toLowerCase();
    if (['finalizado', 'completado'].includes(s)) return 'status-completed';
    if (['cancelado'].includes(s)) return 'status-cancelled';
    if (['creado', 'buscando conductor'].includes(s)) return 'status-pending';
    if (['asignado'].includes(s)) return 'status-assigned';
    if (['en curso', 'comenzado', 'iniciado'].includes(s)) return 'status-ongoing';
    return 'status-pending';
  }

  /** El mapa terminó de inicializarse: pinta si los datos ya llegaron. */
  onMapReady(map?: google.maps.Map): void {
    this.mapReady = true;
    if (map) {
      this.mapInstance = map;
    }
    this.renderMap();
  }

  private renderMap(): void {
    const map = this.mapInstance ?? this.googleMap?.googleMap ?? null;
    if (!this.mapReady || !map || !this.trip) {
      return;
    }

    // Idempotente: limpiar lo anterior por si se vuelve a pintar.
    this.originMarker?.setMap(null);
    this.destinoMarker?.setMap(null);
    this.routePolyline?.setMap(null);
    this.originMarker = null;
    this.destinoMarker = null;
    this.routePolyline = null;

    const origin = {
      lat: this.trip.add_latitude_origin,
      lng: this.trip.add_longitude_origin,
    };
    const destino = {
      lat: this.trip.add_latitude_destination,
      lng: this.trip.add_longitude_destination,
    };

    this.originMarker = new google.maps.Marker({
      position: origin,
      map,
      title: 'Origen',
      icon: this.pinIcon('#F59527'),
      label: { text: 'O', color: '#ffffff', fontSize: '13px', fontWeight: 'bold' },
    });
    this.destinoMarker = new google.maps.Marker({
      position: destino,
      map,
      title: 'Destino',
      icon: this.pinIcon('#10b981'),
      label: { text: 'D', color: '#ffffff', fontSize: '13px', fontWeight: 'bold' },
    });

    const bounds = new google.maps.LatLngBounds();
    bounds.extend(origin);
    bounds.extend(destino);
    map.fitBounds(bounds);

    this.drawRoute(this.trip.car_tri_route);
  }

  private pinIcon(color: string): google.maps.Symbol {
    return {
      path: TripDetailComponent.PIN_PATH,
      fillColor: color,
      fillOpacity: 1,
      strokeColor: '#ffffff',
      strokeWeight: 2,
      scale: 1.2,
      anchor: new google.maps.Point(0, 0),
      labelOrigin: new google.maps.Point(0, -30),
    };
  }

  /**
   * Traza la ruta a partir de car_tri_route (respuesta de Google Routes API).
   * Si no llega en el formato esperado, no dibuja nada (no rompe el flujo).
   */
  private drawRoute(raw: any): void {
    const map = this.mapInstance ?? this.googleMap?.googleMap ?? null;
    if (!map || !raw) return;
    if (typeof google === 'undefined' || !google.maps?.geometry?.encoding) return;

    let obj = raw;
    if (typeof raw === 'string') {
      try {
        obj = JSON.parse(raw);
      } catch {
        return;
      }
    }

    const encoded = obj?.routes?.[0]?.polyline?.encodedPolyline;
    if (!encoded || typeof encoded !== 'string') return;

    let path: google.maps.LatLng[] = [];
    try {
      path = google.maps.geometry.encoding.decodePath(encoded);
    } catch {
      return;
    }
    if (!path.length) return;

    this.routePolyline = new google.maps.Polyline({
      path,
      map,
      strokeColor: '#1f51ba',
      strokeOpacity: 0.9,
      strokeWeight: 5,
    });
  }
}
